import { useQuery } from "@tanstack/react-query";
import { rateSurfConditions, RATING_CONFIG, SurfRating } from "./conditions.js";

const CURRENT_MODEL_VARS = [
  "wave_height",
  "wave_period",
  "wave_direction",
  "sea_surface_temperature",
  "swell_wave_height",
  "swell_wave_period",
  "swell_wave_direction",
] as const;
const HOURLY_MODEL_VARS = [
  "wave_height",
  "wave_period",
  "wave_direction",
  "swell_wave_height",
  "swell_wave_period",
  "swell_wave_direction",
] as const;
const DAILY_MODEL_VARS = [
  "wave_height_max",
  "wave_period_max",
  "wave_direction_dominant",
  "sea_surface_temperature_max",
] as const;

/* ---------- Types ---------- */

export interface OpenMeteoMarineResponse {
  current: {
    wave_height: number; // metres
    wave_period: number; // seconds
    wave_direction: number; // degrees
    sea_surface_temperature?: number; // °C
    swell_wave_height?: number;
    swell_wave_period?: number;
    swell_wave_direction?: number;
  };
  hourly: {
    time: string[];
    wave_height: number[];
    wave_period: number[];
    wave_direction: number[];
    swell_wave_height?: number[];
    swell_wave_period?: number[];
    swell_wave_direction?: number[];
  };
  daily: {
    time: string[];
    wave_height_max: number[];
    wave_period_max: number[];
    wave_direction_dominant: number[];
    sea_surface_temperature_max?: number[];
  };
  models?: Partial<Record<MarineModelSlug, OpenMeteoMarineModelPayload>>;
  modelTelemetryEnabled?: boolean;
  /** Spot's UTC offset — hourly/daily time strings are spot-local with no
   *  offset designator, so this is required to recover absolute instants. */
  utc_offset_seconds?: number;
}

export type MarineModelSlug = "ecmwf_wam" | "ncep_gfswave025" | "meteofrance_wave";

export interface OpenMeteoMarineModelPayload {
  current?: Partial<Record<(typeof CURRENT_MODEL_VARS)[number], number | null>> & {
    time?: string;
    interval?: number;
  };
  hourly?: Partial<Record<(typeof HOURLY_MODEL_VARS)[number], (number | null)[]>> & {
    time: string[];
  };
  daily?: Partial<Record<(typeof DAILY_MODEL_VARS)[number], (number | null)[]>> & {
    time: string[];
  };
}

export interface HourlyMarine {
  time: string;
  /**
   * Surf face height — an estimate of what a surfer would see breaking on
   * the beach, derived from offshore significant wave height via
   * `estimateSurfFaceHeight`. When `breakType` is omitted this equals
   * `offshoreHeightM` (backward compatible with pre-breakType callers),
   * but `useMarineData` defaults to `"beach"` so it's face height in
   * practice.
   */
  waveHeightM: number;
  /** Raw offshore significant wave height — what a buoy would report. */
  offshoreHeightM: number;
  wavePeriod: number | null;
  waveDirection: number;
  swellHeightM: number | null;
  swellPeriod: number | null;
  swellDirection: number | null;
}

export interface MarineModelForecast {
  /** Spot's UTC offset, mirrored from the parent response so per-model
   *  hourly times (spot-local strings) can be resolved to instants. */
  utcOffsetSeconds: number | null;
  offshoreHeightM: number | null;
  wavePeriod: number | null;
  waveDirection: number | null;
  swellHeightM: number | null;
  swellPeriod: number | null;
  swellDirection: number | null;
  hourly: Array<{
    time: string;
    offshoreHeightM: number | null;
    wavePeriod: number | null;
    waveDirection: number | null;
    swellHeightM: number | null;
    swellPeriod: number | null;
    swellDirection: number | null;
  }>;
  daily: Array<{
    date: string;
    offshoreHeightMaxM: number | null;
    wavePeriodMax: number | null;
    waveDirectionDominant: number | null;
    waterTempMaxC: number | null;
  }>;
}

export interface MarineData {
  /** Surf face height — see HourlyMarine.waveHeightM. */
  waveHeightM: number;
  /** Raw offshore significant wave height — what the buoy reports. */
  offshoreHeightM: number;
  wavePeriod: number | null;
  waveDirection: number;
  swellHeightM: number | null;
  swellPeriod: number | null;
  swellDirection: number | null;
  waterTempC: number | null;
  rating: SurfRating;
  ratingLabel: string;
  ratingEmoji: string;
  ratingColor: string;
  /** Spot's UTC offset for resolving the local-time strings in
   *  `hourly`/`daily` to absolute instants; null for cached responses
   *  predating the field. */
  utcOffsetSeconds: number | null;
  hourly: HourlyMarine[];
  daily: {
    date: string;
    /** Surf face height at day's peak. */
    waveHeightMaxM: number;
    /** Offshore significant wave height at day's peak. */
    offshoreHeightMaxM: number;
    wavePeriodMax: number | null;
    waveDirectionDominant: number;
    waterTempMaxC: number | null;
  }[];
  models?: Partial<Record<MarineModelSlug, MarineModelForecast>>;
  modelTelemetryEnabled?: boolean;
}

/* ---------- Nearshore Wave Approximation ---------- */

/**
 * Break type affects how waves transform approaching shore.
 * - beach: sand bottom, gradual slope → moderate shoaling
 * - reef: shallow reef shelf → sharper shoaling, waves jack up more
 * - point: headland refraction → moderate shoaling + focusing
 */
export type BreakType = "beach" | "reef" | "point";

/**
 * Period-dependent factor mapping offshore significant wave height to
 * "surf face" height at the break.
 *
 * The previous implementation modelled shoaling only — waves getting
 * taller as the bottom shallows — and clipped at 1.0 even for short
 * wind-swell. In practice the open-ocean-to-face ratio for short-period
 * chop is much less than 1: the waves dissipate, break early, and lose
 * energy to whitewater before reaching a clean wall. That's why
 * competitors reported 3–4 ft "surf" at Flagler when the buoy was
 * sitting at 6.6 ft @ 6s.
 *
 * This factor combines both regimes in a single curve:
 *   ≤5s   windswell       → 0.55  (significant dissipation)
 *   5–8s  mixed            → 0.60–0.80
 *   8–12s medium groundswell → 0.90–1.10
 *   12–16s long groundswell  → 1.10–1.25
 *   >16s  epic long-period   → 1.30
 *
 * Plus break-type modifiers for reef / point breaks where long-period
 * ground swell jacks up on shallow rock. Still a heuristic, not a
 * bathymetric model, but it puts the output within ~20% of Surfline's
 * LOTUS estimate for the Flagler-style cases we can compare against.
 */
export function surfFaceFactor(periodS: number, breakType: BreakType = "beach"): number {
  let f: number;
  if (periodS <= 5) f = 0.55;
  else if (periodS <= 8)
    f = 0.6 + ((periodS - 5) / 3) * 0.2; // 0.60 → 0.80
  else if (periodS <= 12)
    f = 0.9 + ((periodS - 8) / 4) * 0.2; // 0.90 → 1.10
  else if (periodS <= 16)
    f = 1.1 + ((periodS - 12) / 4) * 0.15; // 1.10 → 1.25
  else f = 1.3;

  // Reef/point amplification only kicks in meaningfully for groundswell;
  // short-period chop still dissipates even over rock.
  if (periodS >= 10) {
    if (breakType === "reef") f *= 1.1;
    if (breakType === "point") f *= 1.05;
  }

  return Math.round(f * 100) / 100;
}

/**
 * Estimate surf face height at the break from offshore significant wave
 * height + peak period.
 */
export function estimateSurfFaceHeight(
  offshoreHeightM: number,
  periodS: number,
  breakType: BreakType = "beach",
): number {
  const f = surfFaceFactor(periodS, breakType);
  return Math.round(offshoreHeightM * f * 100) / 100;
}

/** @deprecated — renamed to `surfFaceFactor`. Keeps existing imports working. */
export const shoalingCoefficient = surfFaceFactor;
/** @deprecated — renamed to `estimateSurfFaceHeight`. */
export const estimateBreakingHeight = estimateSurfFaceHeight;

/* ---------- Time helpers ---------- */

/**
 * Convert an Open-Meteo spot-local time string (no offset designator,
 * e.g. `"2026-06-11T14:00"`) to a `Z`-suffixed UTC ISO string using the
 * response's `utc_offset_seconds`. Strings that already carry an
 * explicit offset are normalized to UTC as-is. Display code should keep
 * using the raw local strings; this is for telemetry and any other
 * cross-timezone comparison.
 */
export function localTimeToUtcIso(time: string, utcOffsetSeconds: number): string {
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(time)) return new Date(time).toISOString();
  const ms = Date.parse(`${time}Z`);
  if (!Number.isFinite(ms)) return time;
  return new Date(ms - utcOffsetSeconds * 1000).toISOString();
}

/* ---------- Constants ---------- */

const METERS_TO_FEET = 3.28084;

/* ---------- Fetcher ---------- */

export interface MarineFetchOptions {
  includeModels?: boolean;
  /** Stable region id — required to opt into server-side bias correction. */
  regionId?: string;
}

async function fetchMarineData(
  lat: number,
  lon: number,
  options: MarineFetchOptions = {},
): Promise<OpenMeteoMarineResponse> {
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
  });
  if (options.includeModels) params.set("includeModels", "1");
  if (options.regionId) params.set("regionId", options.regionId);
  const url = `/api/marine?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Marine API error ${res.status}`);
  return res.json();
}

/* ---------- Transformer ---------- */

function toSurfFace(offshoreM: number, periodS: number | null, breakType?: BreakType) {
  if (!breakType) return offshoreM;
  return estimateSurfFaceHeight(offshoreM, periodS ?? 8, breakType);
}

function transformMarineModels(
  models?: Partial<Record<MarineModelSlug, OpenMeteoMarineModelPayload>>,
  utcOffsetSeconds: number | null = null,
): Partial<Record<MarineModelSlug, MarineModelForecast>> | undefined {
  if (!models) return undefined;

  const transformed: Partial<Record<MarineModelSlug, MarineModelForecast>> = {};
  for (const [model, payload] of Object.entries(models) as [
    MarineModelSlug,
    OpenMeteoMarineModelPayload,
  ][]) {
    const current = payload.current;
    const hourly = payload.hourly;
    const daily = payload.daily;
    transformed[model] = {
      utcOffsetSeconds,
      offshoreHeightM: current?.wave_height ?? null,
      wavePeriod: current?.wave_period ?? null,
      waveDirection: current?.wave_direction ?? null,
      swellHeightM: current?.swell_wave_height ?? null,
      swellPeriod: current?.swell_wave_period ?? null,
      swellDirection: current?.swell_wave_direction ?? null,
      hourly: (hourly?.time ?? []).map((time, i) => ({
        time,
        offshoreHeightM: hourly?.wave_height?.[i] ?? null,
        wavePeriod: hourly?.wave_period?.[i] ?? null,
        waveDirection: hourly?.wave_direction?.[i] ?? null,
        swellHeightM: hourly?.swell_wave_height?.[i] ?? null,
        swellPeriod: hourly?.swell_wave_period?.[i] ?? null,
        swellDirection: hourly?.swell_wave_direction?.[i] ?? null,
      })),
      daily: (daily?.time ?? []).map((date, i) => ({
        date,
        offshoreHeightMaxM: daily?.wave_height_max?.[i] ?? null,
        wavePeriodMax: daily?.wave_period_max?.[i] ?? null,
        waveDirectionDominant: daily?.wave_direction_dominant?.[i] ?? null,
        waterTempMaxC: daily?.sea_surface_temperature_max?.[i] ?? null,
      })),
    };
  }

  return Object.keys(transformed).length > 0 ? transformed : undefined;
}

function transformMarineData(
  raw: OpenMeteoMarineResponse,
  breakType?: BreakType,
): MarineData {
  const period = raw.current.wave_period;
  const offshoreHeightM = raw.current.wave_height;
  const waveHeightM = toSurfFace(offshoreHeightM, period, breakType);
  const waveDirection = raw.current.wave_direction;
  const swellHeightM = raw.current.swell_wave_height ?? null;
  const swellPeriod = raw.current.swell_wave_period ?? null;
  const swellDirection = raw.current.swell_wave_direction ?? null;
  const waterTempC = raw.current.sea_surface_temperature ?? null;

  // Base-only rating uses the surf face height (what the surfer actually
  // experiences). Components should call rateSurfQuality() directly with
  // merged marine + weather data for a wind-aware score.
  const waveHeightFt = waveHeightM * METERS_TO_FEET;
  const rating = rateSurfConditions(waveHeightFt, period);
  const config = RATING_CONFIG[rating];

  const hourly: HourlyMarine[] = raw.hourly.time.map((time, i) => {
    const hp = raw.hourly.wave_period[i];
    const offshore = raw.hourly.wave_height[i];
    return {
      time,
      waveHeightM: toSurfFace(offshore, hp, breakType),
      offshoreHeightM: offshore,
      wavePeriod: hp,
      waveDirection: raw.hourly.wave_direction[i],
      swellHeightM: raw.hourly.swell_wave_height?.[i] ?? null,
      swellPeriod: raw.hourly.swell_wave_period?.[i] ?? null,
      swellDirection: raw.hourly.swell_wave_direction?.[i] ?? null,
    };
  });

  // Daily face height comes from the day's hourly face heights rather
  // than pairing wave_height_max with wave_period_max — the two daily
  // maxima can occur at different hours, which overestimates the face.
  const dailyFaceMaxM = new Map<string, number>();
  for (const h of hourly) {
    const day = h.time.slice(0, 10);
    const prev = dailyFaceMaxM.get(day);
    if (prev == null || h.waveHeightM > prev) dailyFaceMaxM.set(day, h.waveHeightM);
  }

  const daily = raw.daily.time.map((date, i) => {
    const dp = raw.daily.wave_period_max[i];
    const offshore = raw.daily.wave_height_max[i];
    return {
      date,
      waveHeightMaxM: dailyFaceMaxM.get(date) ?? toSurfFace(offshore, dp, breakType),
      offshoreHeightMaxM: offshore,
      wavePeriodMax: dp,
      waveDirectionDominant: raw.daily.wave_direction_dominant[i],
      waterTempMaxC: raw.daily.sea_surface_temperature_max?.[i] ?? null,
    };
  });

  const utcOffsetSeconds =
    typeof raw.utc_offset_seconds === "number" ? raw.utc_offset_seconds : null;

  return {
    waveHeightM,
    offshoreHeightM,
    wavePeriod: period,
    waveDirection,
    swellHeightM,
    swellPeriod,
    swellDirection,
    waterTempC,
    rating,
    ratingLabel: config.label,
    ratingEmoji: config.emoji,
    ratingColor: config.color,
    utcOffsetSeconds,
    hourly,
    daily,
    models: transformMarineModels(raw.models, utcOffsetSeconds),
    modelTelemetryEnabled: raw.modelTelemetryEnabled === true,
  };
}

/* ---------- Hook ---------- */

export function useMarineData(
  lat: number | null,
  lon: number | null,
  breakType: BreakType = "beach",
  options: MarineFetchOptions = {},
) {
  const includeModels = options.includeModels === true;
  const regionId = options.regionId ?? null;
  return useQuery({
    queryKey: [
      "marine",
      lat,
      lon,
      breakType,
      includeModels ? "models" : "ensemble",
      // regionId becomes part of the cache key so two regions that
      // resolve to the same lat/lon don't share a bias-corrected
      // response. Null fall-through covers callers that haven't opted in.
      regionId,
    ],
    queryFn: () =>
      fetchMarineData(lat!, lon!, {
        includeModels,
        regionId: regionId ?? undefined,
      }),
    enabled: lat !== null && lon !== null,
    select: (raw) => transformMarineData(raw, breakType),
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchInterval: 4 * 60 * 60 * 1000,
    retry: 2,
  });
}
